import React, { useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { DataContext } from '../data/DataContext';
import { DataContextType } from '../data_types/interfaces';

import startsWith from 'lodash/startsWith';

import AppBar from '@mui/material/AppBar';
import Settings from '@mui/icons-material/Settings';
import Button from '@mui/material/Button';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import ListItemIcon from '@mui/material/ListItemIcon';
import LockIcon from '@mui/icons-material/Lock';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import MenuItem from '@mui/material/MenuItem';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Divider from '@mui/material/Divider';

import { useLogout } from '../buttons/Logout';

import DGVMetricsLogo from "../images/DGVMetricsLogo";

const getNavBtnSx = (isActive: boolean) => ({
    my: 1,
    px: 2,
    borderRadius: '20px',
    fontWeight: 600,
    fontSize: '0.82rem',
    textTransform: 'none' as const,
    letterSpacing: '0.2px',
    minWidth: 0,
    transition: 'all 0.2s ease',
    color: isActive ? 'primary.main' : 'text.secondary',
    background: isActive ? 'rgba(0, 167, 111, 0.12)' : 'transparent',
    border: isActive ? '1px solid rgba(0, 167, 111, 0.25)' : '1px solid transparent',
    '&:hover': {
        background: isActive ? 'rgba(0, 167, 111, 0.18)' : 'rgba(255,255,255,0.05)',
        color: isActive ? 'primary.main' : 'text.primary',
        border: isActive ? '1px solid rgba(0, 167, 111, 0.35)' : '1px solid rgba(255,255,255,0.08)',
    },
});

const menuPaperSx = {
    background: 'rgba(28, 37, 48, 0.97)',
    backdropFilter: 'blur(12px)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '12px',
    mt: 1,
    minWidth: 160,
};

interface ClubBtnProps {
    isClubMember: boolean;
    navigate: (route: string) => void;
    pathname: string;
    label: string;
    route: string;
}

const ClubButton = ({ isClubMember, navigate, route, pathname, label }: ClubBtnProps) => {
    const isActive = pathname === route;
    const btnSx = getNavBtnSx(isActive);

    if (isClubMember) {
        return (
            <Button onClick={() => navigate(route)} sx={btnSx}>
                {label}
            </Button>
        );
    }

    return (
        <Tooltip
            title="Subscribe to DGV Club to activate"
            slotProps={{
                popper: {
                    modifiers: [{
                        name: 'offset',
                        options: { offset: [0, -20] },
                    }],
                },
            }}
        >
            <span>
                <Button
                    onClick={() => navigate(route)}
                    sx={{ ...btnSx, color: 'text.disabled' }}
                >
                    <LockIcon sx={{ fontSize: '0.95rem', mr: 0.6, color: 'warning.main' }} />
                    {label}
                </Button>
            </span>
        </Tooltip>
    );
};


const TopBar = () => {
    const context = useContext<DataContextType | undefined>(DataContext);
    const { isAdmin, isCreator, isClubMember } = context || {};

    const { logout, disabled } = useLogout();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const [mobileNavAnchoEl, setMobileNavAnchorEl] = React.useState<HTMLInputElement | null>(null);
    const [settingsAnchorEl, setSettingsAnchorEl] = React.useState<HTMLInputElement | null>(null);

    const handleOpenMobileMenu = (event: React.SyntheticEvent) =>
        setMobileNavAnchorEl(event.currentTarget as HTMLInputElement);
    const handleCloseMobileMenu = () => setMobileNavAnchorEl(null);

    const handleOpenUserSettingsMenu = (event: React.SyntheticEvent) =>
        setSettingsAnchorEl(event.currentTarget as HTMLInputElement);
    const handleCloseUserSettingsMenu = () => setSettingsAnchorEl(null);

    return (
        <AppBar
            elevation={0}
            position="static"
            sx={{
                backgroundColor: 'rgba(22, 28, 36, 0.95)',
                backdropFilter: 'blur(12px)',
                marginBottom: '30px',
                position: 'relative',
                borderBottom: '1px solid rgba(255,255,255,0.04)',
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, rgba(0,167,111,0) 0%, rgba(0,167,111,0.8) 25%, rgba(0,184,217,0.8) 55%, rgba(142,51,255,0.5) 80%, rgba(142,51,255,0) 100%)',
                    animation: 'shimmerBar 4s ease-in-out infinite',
                    '@keyframes shimmerBar': {
                        '0%, 100%': { opacity: 0.7 },
                        '50%': { opacity: 1 },
                    },
                },
            }}
        >
            <Container maxWidth="xl">
                <Toolbar disableGutters>
                    {/* Desktop logo */}
                    <Box
                        component="div"
                        sx={{
                            mr: 3,
                            display: { xs: 'none', md: 'flex' },
                            alignItems: 'center',
                        }}
                    >
                        <DGVMetricsLogo topNavLogo={true} />
                    </Box>

                    {/* Mobile hamburger */}
                    <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
                        <IconButton
                            size="large"
                            aria-label="navigation menu"
                            aria-controls="mobile-menu"
                            aria-haspopup="true"
                            onClick={handleOpenMobileMenu}
                            sx={{
                                color: 'text.secondary',
                                '&:hover': { color: 'text.primary', background: 'rgba(255,255,255,0.06)' },
                            }}
                        >
                            <MenuIcon />
                        </IconButton>

                        <Menu
                            keepMounted
                            id="mobile-menu"
                            anchorEl={mobileNavAnchoEl}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            open={Boolean(mobileNavAnchoEl)}
                            onClose={handleCloseMobileMenu}
                            PaperProps={{ sx: { ...menuPaperSx, minWidth: 200 } }}
                            sx={{ display: { xs: 'block', md: 'none' } }}
                        >
                            <MenuItem
                                selected={pathname === '/'}
                                onClick={() => { handleCloseMobileMenu(); navigate('/'); }}
                                sx={{ borderRadius: '8px', mx: 0.5, my: 0.25 }}
                            >
                                <Typography>Summary</Typography>
                            </MenuItem>
                            <MenuItem
                                selected={startsWith(pathname, '/period')}
                                onClick={() => { handleCloseMobileMenu(); navigate('/period'); }}
                                sx={{ borderRadius: '8px', mx: 0.5, my: 0.25 }}
                            >
                                <Typography>By Period</Typography>
                            </MenuItem>
                            <Divider sx={{ my: 0.5, borderColor: 'rgba(255,255,255,0.07)' }} />
                            <MenuItem
                                selected={pathname === '/opponents'}
                                onClick={() => { handleCloseMobileMenu(); navigate('/opponents'); }}
                                sx={{ borderRadius: '8px', mx: 0.5, my: 0.25 }}
                            >
                                {isClubMember
                                    ? <Typography>Opponents</Typography>
                                    : <Typography sx={{ display: 'flex', alignItems: 'center', color: 'text.disabled' }}><LockIcon sx={{ fontSize: '1rem', mr: 0.75, color: 'warning.main' }} />Opponents</Typography>
                                }
                            </MenuItem>
                            <MenuItem
                                selected={pathname === '/holes'}
                                onClick={() => { handleCloseMobileMenu(); navigate('/holes'); }}
                                sx={{ borderRadius: '8px', mx: 0.5, my: 0.25 }}
                            >
                                {isClubMember
                                    ? <Typography>Holes</Typography>
                                    : <Typography sx={{ display: 'flex', alignItems: 'center', color: 'text.disabled' }}><LockIcon sx={{ fontSize: '1rem', mr: 0.75, color: 'warning.main' }} />Holes</Typography>
                                }
                            </MenuItem>
                        </Menu>
                    </Box>

                    {/* Mobile logo */}
                    <Box component="div" sx={{ mr: 2, display: { xs: 'flex', md: 'none' }, flexGrow: 1 }}>
                        <DGVMetricsLogo topNavLogo={true} />
                    </Box>

                    {/* Desktop nav links */}
                    <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
                        <Button
                            onClick={() => navigate('/')}
                            sx={getNavBtnSx(pathname === '/')}
                        >
                            Summary
                        </Button>

                        <Button
                            onClick={() => navigate('/period')}
                            sx={getNavBtnSx(startsWith(pathname, '/period'))}
                        >
                            By Period
                        </Button>

                        <ClubButton
                            isClubMember={!!isClubMember}
                            navigate={navigate}
                            pathname={pathname}
                            route="/opponents"
                            label="Opponents"
                        />

                        <ClubButton
                            isClubMember={!!isClubMember}
                            navigate={navigate}
                            pathname={pathname}
                            route="/holes"
                            label="Holes"
                        />
                    </Box>

                    {/* User settings button */}
                    <Box sx={{ flexGrow: 0 }}>
                        <IconButton
                            onClick={handleOpenUserSettingsMenu}
                            sx={{
                                p: 0.75,
                                borderRadius: '10px',
                                border: '1px solid',
                                borderColor: Boolean(settingsAnchorEl) ? 'rgba(0,167,111,0.4)' : 'rgba(255,255,255,0.1)',
                                background: Boolean(settingsAnchorEl) ? 'rgba(0,167,111,0.1)' : 'rgba(255,255,255,0.04)',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    background: 'rgba(255,255,255,0.08)',
                                    borderColor: 'rgba(255,255,255,0.2)',
                                },
                            }}
                        >
                            <Settings sx={{ fontSize: 20, color: Boolean(settingsAnchorEl) ? 'primary.main' : 'text.secondary' }} />
                        </IconButton>

                        <Menu
                            keepMounted
                            sx={{ mt: '8px' }}
                            id="settings-menu"
                            anchorEl={settingsAnchorEl}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            open={Boolean(settingsAnchorEl)}
                            onClose={handleCloseUserSettingsMenu}
                            PaperProps={{ sx: { ...menuPaperSx, minWidth: 180 } }}
                        >
                            {(isAdmin || isCreator) && (
                                <MenuItem
                                    disabled={disabled}
                                    onClick={() => { handleCloseUserSettingsMenu(); navigate('/admin/dashboard'); }}
                                    sx={{ borderRadius: '8px', mx: 0.5, my: 0.25 }}
                                >
                                    <ListItemIcon><LockIcon fontSize="small" /></ListItemIcon>
                                    Admin
                                </MenuItem>
                            )}
                            <MenuItem
                                disabled={disabled}
                                onClick={() => { handleCloseUserSettingsMenu(); navigate('/profile'); }}
                                sx={{ borderRadius: '8px', mx: 0.5, my: 0.25 }}
                            >
                                <ListItemIcon><AccountCircleIcon fontSize="small" /></ListItemIcon>
                                Profile
                            </MenuItem>
                            <Divider sx={{ my: 0.5, borderColor: 'rgba(255,255,255,0.07)' }} />
                            <MenuItem
                                disabled={disabled}
                                onClick={() => { handleCloseUserSettingsMenu(); logout(); }}
                                sx={{ borderRadius: '8px', mx: 0.5, my: 0.25, color: 'error.main' }}
                            >
                                <ListItemIcon>
                                    <LogoutIcon fontSize="small" sx={{ color: 'error.main' }} />
                                </ListItemIcon>
                                Log out
                            </MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </Container>
        </AppBar>
    );
}

export default TopBar;
